import { createRouter, createWebHistory } from 'vue-router'
import { useAppDataStore } from '@/stores/app'
import { useTripBuilderStore } from '@/stores/trip-builder'
import type { ServiceInterface, TripInterface } from '@/types/trips'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'planner',
      component: () => import('../views/trips/TripManager.vue'),
    },
    {
      path: '/trip/:uuid',
      name: 'trip',
      component: () => import('../views/trips/TripPlanner.vue'),
      beforeEnter: (to, _from, next) => {
        const appData = useAppDataStore()
        const incomingUuid = to.params.uuid?.toString() ?? ''

        if (incomingUuid === 'new') {
          // New trip: initialise stub, no lock needed yet
          appData.trip = useTripBuilderStore().newTrip()
        } else if (appData.trip?.uuid && appData.trip.uuid !== incomingUuid) {
          // Navigating from one open trip directly to another.
          // Release the existing lock before fetching the new trip.
          appData.releaseLock(appData.trip)
            .finally(() => appData.updateTripContext({ uuid: incomingUuid } as TripInterface))
        } else if (!appData.trip) {
          // Normal entry: no trip loaded yet
          appData.updateTripContext({ uuid: incomingUuid } as TripInterface)
        }

        next()
      }
    },
    {
      path: '/suppliers',
      name: 'suppliers',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/suppliers/SupplierManager.vue')
    },
    {
      path: '/suppliers/:uuid',
      name: 'supplierEditor',
      props: true,
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/suppliers/SupplierEditor.vue'),
      beforeEnter: (to, from, next) => {
        const appData = useAppDataStore()
        if (!appData.service && to.params?.uuid && (to.params?.uuid !== 'new')) {
          appData.fetchSupplier({ uuid: to.params?.uuid.toString() })
        }

        next()
      }
    },
    {
      path: '/packages',
      name: 'packages',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/bundles/PackagesList.vue')
    },
    {
      path: '/package/editor/:uuid',
      name: 'packageEditor',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/bundles/PackageEditor.vue'),
      beforeEnter: (to, from, next) => {
        const appData = useAppDataStore()

        if (!appData.service && to.params?.uuid && (to.params?.uuid !== 'new')) {
          appData.fetchPackage({ uuid: to.params?.uuid.toString() })
        }

        next()
      },
    },
    {
      path: '/services',
      name: 'services',
      component: () => import('../views/products/ProductManager.vue'),
    },
    {
      path: '/services/:uuid',
      name: 'serviceEditor',
      props: true,
      component: () => import('../views/products/ProductEditor.vue'),
      beforeEnter: (to, from, next) => {
        const appData = useAppDataStore()
        console.debug("router::beforeEnter | ", appData.service);
        if (!appData.service && to.params?.uuid && (to.params?.uuid !== 'new')) {
          appData.fetchService({ uuid: to.params?.uuid.toString() })
        }
        else if (!appData.service && to.params?.uuid === 'new') {
          appData.service = { pricingType: 'per-person' } as ServiceInterface
        }

        next()
      }
    },
    {
      path: '/contacts',
      name: 'contacts',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/ContactsView.vue')
    },
    {
      path: '/media',
      name: 'media',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/MediaLibrary.vue')
    },
    {
      path: '/users',
      name: 'users',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/UserManager.vue')
    },
    {
      path: '/locations',
      name: 'locations',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/LocationManager.vue'),
      beforeEnter: (to, from, next) => {
        useAppDataStore().fetchCountries();

        next()
      }
    },
    {
      path: '/settings',
      name: 'settings',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/SettingsEditor.vue')
    }
  ]
})

export default router;
